import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, tap, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiEnvelope, ApiGateConfig, ApiGateDisplay, GateMode } from '../api/api.types';
import { PlatformContactService } from './platform-contact.service';
import { PlatformSocialService } from './platform-social.service';

/**
 * Reads the portal's gate state from the public app-config endpoint and
 * exposes it as signals the app shell gates on.
 *
 * Two separate questions, both answered by the one call: `active` is whether
 * to show a gate at all, `mode` is which gate to show.
 *
 * Fails open: any error, or a response that takes too long, leaves the portal
 * usable rather than locking everyone out over a transient backend hiccup.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private readonly contact = inject(PlatformContactService);
  private readonly social = inject(PlatformSocialService);

  /** True when the portal is under maintenance and should be gated. */
  readonly active = signal(false);
  /** True while a check is in flight (drives the "Check again" spinner). */
  readonly checking = signal(false);
  /** Which view the gate renders. Only meaningful while `active` is true. */
  readonly mode = signal<GateMode>('maintenance');
  /**
   * The wording a visitor agrees to when joining the prelaunch list.
   *
   * Served rather than hardcoded because the API stores this exact string
   * against each signup: wording in the template that had drifted from the
   * server's would make every consent record describe something the visitor
   * was never shown.
   */
  readonly consentText = signal('');
  readonly heading = signal('');
  readonly message = signal('');
  readonly countdownEnabled = signal(false);
  readonly endsAt = signal<string | null>(null);

  check(): Observable<boolean> {
    this.checking.set(true);
    return this.http.get<ApiEnvelope<ApiGateConfig>>(`${environment.apiUrl}/core/app-config`).pipe(
      timeout(4000),
      map((res) => res?.data ?? null),
      catchError(() => of(null)),
      tap((config) => {
        this.active.set(!!config?.maintenance?.portal);
        // Anything unrecognised, including a failed request, falls back to the
        // plain maintenance page. A backend hiccup should never be the reason
        // visitors are shown a form asking for their details.
        const rawMode = config?.gate_mode?.portal;
        this.mode.set(rawMode === 'prelaunch' || rawMode === 'waitlist' ? 'prelaunch' : 'maintenance');
        this.consentText.set(
          config?.prelaunch?.consent_text ?? config?.waitlist?.consent_text ?? ''
        );
        this.applyDisplay(config?.gate_display?.portal);
        this.contact.apply(config?.support_contact);
        this.social.apply(config?.social_links);
        this.checking.set(false);
      }),
      map((config) => !!config?.maintenance?.portal)
    );
  }

  private applyDisplay(display: ApiGateDisplay | null | undefined): void {
    this.heading.set(display?.heading?.trim() ?? '');
    this.message.set(display?.message?.trim() ?? '');
    this.countdownEnabled.set(!!display?.countdown_enabled && !!display?.ends_at);
    this.endsAt.set(display?.ends_at ?? null);
  }
}
